import { supabase } from '../../lib/supabase';

// Phase 126's RPCs are not in the (stale) generated Database types yet, so they
// go through the project-wide escape hatch used by grading-api / tests-api.
// Drop this once `supabase gen types` has been re-run.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export const ADMIN_QUERY_KEYS = {
  institutes: ['admin', 'institutes'] as const,
  defaultInstitute: ['admin', 'default_institute'] as const,
  pendingInstitutes: ['admin', 'institutes', 'pending'] as const,
  instituteMembers: (instituteId: string) => ['admin', 'institute_members', instituteId] as const,
  instituteStudents: (instituteId: string) => ['admin', 'institute_students', instituteId] as const,
  institute: (instituteId: string) => ['admin', 'institute', instituteId] as const,
  superadmins: ['admin', 'superadmins'] as const,
};

export interface InstituteRow {
  id: string;
  name: string;
  slug: string;
  owner_email: string;
  owner_phone: string | null;
  status: 'pending' | 'active' | 'suspended';
  created_at: string;
  updated_at: string;
}

export interface MemberRow {
  id: string;
  user_id: string;
  email: string;
  role: 'owner' | 'admin' | 'teacher' | 'examiner';
  status: 'pending' | 'active' | 'suspended';
  joined_at: string | null;
  created_at: string;
}

export interface StudentRow {
  id: string;
  user_id: string;
  email: string;
  roll_no: string | null;
  exam_type: string;
  target_band: number | null;
  created_at: string;
}

export interface SuperadminRow {
  user_id: string;
  email: string;
  added_by: string | null;
  added_at: string;
}

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('Empty response from Supabase');
  return data;
}

export async function listInstitutes(): Promise<InstituteRow[]> {
  const { data, error } = await supabase
    .from('institutes')
    .select('id, name, slug, owner_email, owner_phone, status, created_at, updated_at')
    .order('created_at', { ascending: false });
  return unwrap(data as InstituteRow[] | null, error);
}

export interface PendingInstituteRow extends InstituteRow {
  owner_name: string | null;
  owner_user_id: string | null;
}

// Pending applications queued by request-institute. Joined with the pending
// owner member to surface owner identity in the approval UI.
export async function listPendingInstitutes(): Promise<PendingInstituteRow[]> {
  const { data, error } = await supabase
    .from('institutes')
    .select(
      'id, name, slug, owner_email, owner_phone, status, created_at, updated_at, institute_members!inner(user_id, role, status)',
    )
    .eq('status', 'pending')
    .eq('institute_members.role', 'owner')
    .order('created_at', { ascending: false });
  if (error !== null) throw new Error(error.message);
  const rows = (data ?? []) as Array<
    InstituteRow & {
      institute_members: Array<{ user_id: string; role: string; status: string }>;
    }
  >;
  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    slug: row.slug,
    owner_email: row.owner_email,
    owner_phone: row.owner_phone,
    status: row.status,
    created_at: row.created_at,
    updated_at: row.updated_at,
    owner_name: null,
    owner_user_id: row.institute_members[0]?.user_id ?? null,
  }));
}

export interface ApproveInstituteResult {
  institute_id: string;
  owner_user_id: string;
  status: 'active';
}

export async function approveInstitute(instituteId: string): Promise<ApproveInstituteResult> {
  const { data, error } = await supabase.functions.invoke<ApproveInstituteResult>(
    'approve-institute',
    { body: { institute_id: instituteId } },
  );
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('approve-institute returned no body');
  return data;
}

export interface RejectInstituteResult {
  institute_id: string;
  status: 'rejected_and_deleted';
}

export async function rejectInstitute(
  instituteId: string,
  reason: string,
): Promise<RejectInstituteResult> {
  const { data, error } = await supabase.functions.invoke<RejectInstituteResult>(
    'reject-institute',
    { body: { institute_id: instituteId, reason } },
  );
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('reject-institute returned no body');
  return data;
}

export async function getInstitute(instituteId: string): Promise<InstituteRow> {
  const { data, error } = await supabase
    .from('institutes')
    .select('id, name, slug, owner_email, owner_phone, status, created_at, updated_at')
    .eq('id', instituteId)
    .limit(1)
    .maybeSingle();
  return unwrap(data as InstituteRow | null, error);
}

export async function listInstituteMembers(instituteId: string): Promise<MemberRow[]> {
  const { data, error } = await supabase.rpc('admin_view_institute_members', {
    p_institute_id: instituteId,
  });
  return unwrap(data as MemberRow[] | null, error);
}

export async function listInstituteStudents(instituteId: string): Promise<StudentRow[]> {
  const { data, error } = await supabase.rpc('admin_view_institute_students', {
    p_institute_id: instituteId,
  });
  return unwrap(data as StudentRow[] | null, error);
}

export async function listSuperadmins(): Promise<SuperadminRow[]> {
  const { data, error } = await supabase.rpc('admin_view_superadmins');
  return unwrap(data as SuperadminRow[] | null, error);
}

// ---- Phase 126: the institute new sign-ups are enrolled into ----

export interface DefaultInstituteRow {
  institute_id: string;
  name: string;
  slug: string;
  status: string;
}

/** null when no default has been chosen yet — new sign-ups cannot be placed. */
export async function getDefaultInstitute(): Promise<DefaultInstituteRow | null> {
  const { data, error } = await rpcUnknown.rpc('admin_get_default_institute');
  if (error !== null) throw new Error(error.message);
  const rows = (data as DefaultInstituteRow[] | null) ?? [];
  return rows[0] ?? null;
}

export async function setDefaultInstitute(instituteId: string): Promise<DefaultInstituteRow> {
  const { data, error } = await rpcUnknown.rpc('admin_set_default_institute', {
    p_institute_id: instituteId,
  });
  if (error !== null) throw new Error(error.message);
  const rows = (data as Omit<DefaultInstituteRow, 'status'>[] | null) ?? [];
  const row = rows[0];
  if (row === undefined) throw new Error('Could not set the default institute');
  return { ...row, status: 'active' };
}

export interface CreateInstituteArgs {
  name: string;
  slug: string;
  owner_email: string;
}

export async function createInstitute(args: CreateInstituteArgs): Promise<InstituteRow> {
  const { data, error } = await supabase.rpc('admin_create_institute', {
    p_name: args.name,
    p_slug: args.slug,
    p_owner_email: args.owner_email,
  });
  return unwrap(data as InstituteRow | null, error);
}

export interface AddMemberArgs {
  institute_id: string;
  email: string;
  role: 'owner' | 'admin' | 'teacher' | 'examiner';
}

export async function addMember(args: AddMemberArgs): Promise<MemberRow> {
  const { data, error } = await supabase.rpc('admin_add_member', {
    p_institute_id: args.institute_id,
    p_email: args.email,
    p_role: args.role,
  });
  return unwrap(data as MemberRow | null, error);
}

export interface AddStudentArgs {
  institute_id: string;
  email: string;
  exam_type: 'academic' | 'general';
  roll_no: string | null;
  target_band: number | null;
}

export async function addStudent(args: AddStudentArgs): Promise<StudentRow> {
  const { data, error } = await supabase.rpc('admin_add_student', {
    p_institute_id: args.institute_id,
    p_email: args.email,
    p_exam_type: args.exam_type,
    p_roll_no: args.roll_no,
    p_target_band: args.target_band,
  });
  return unwrap(data as StudentRow | null, error);
}

export async function addSuperadmin(email: string): Promise<SuperadminRow> {
  const { data, error } = await supabase.rpc('admin_add_superadmin', {
    p_email: email,
  });
  return unwrap(data as SuperadminRow | null, error);
}

export interface ProvisionUserResult {
  user_id: string;
  email: string;
  created: boolean;
}

// Calls the admin-provision-user Edge Function. Creates an auth account with
// the supplied password (or updates an existing user's password), and marks
// phone_verified so the user can log in directly without the dev OTP flow.
export async function provisionUser(args: {
  email: string;
  password: string;
}): Promise<ProvisionUserResult> {
  const { data, error } = await supabase.functions.invoke<ProvisionUserResult>(
    'admin-provision-user',
    { body: args },
  );
  if (error !== null) {
    throw new Error(error.message);
  }
  if (data === null) {
    throw new Error('admin-provision-user returned no body');
  }
  return data;
}
